import React, { useContext, useMemo, useState } from "react";
import "../css/shop.scss";
import { Link } from "react-router-dom";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";

const sortOptions = [
  { value: "featured", label: "Featured" },
  { value: "most-relevant", label: "Most relevant" },
  { value: "best-selling", label: "Best selling" },
  { value: "title-ascending", label: "Alphabetically, A-Z" },
  { value: "title-descending", label: "Alphabetically, Z-A" },
  { value: "price-ascending", label: "Price, low to high" },
  { value: "price-descending", label: "Price, high to low" },
  { value: "oldest", label: "Date, old to new" },
  { value: "newest", label: "Date, new to old" },
];

const formatPrice = (price) =>
  `$${Number(price).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function Shop() {
  const { addToCart } = useContext(CartContext);
  const [activeCategory, setActiveCategory] = useState("All Products");
  const [sortBy, setSortBy] = useState("featured");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [addedProductId, setAddedProductId] = useState(null);

  const categories = useMemo(
    () => [
      "All Products",
      ...new Set(productData.map((item) => item.category)),
    ],
    []
  );

  const visibleProducts = useMemo(() => {
    const filtered =
      activeCategory === "All Products"
        ? [...productData]
        : productData.filter((item) => item.category === activeCategory);

    switch (sortBy) {
      case "title-ascending":
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case "title-descending":
        return filtered.sort((a, b) => b.name.localeCompare(a.name));
      case "price-ascending":
        return filtered.sort((a, b) => Number(a.price) - Number(b.price));
      case "price-descending":
        return filtered.sort((a, b) => Number(b.price) - Number(a.price));
      case "best-selling":
        return filtered.sort((a, b) => Number(b.reviews) - Number(a.reviews));
      case "oldest":
        return filtered.sort((a, b) => a.id - b.id);
      case "newest":
        return filtered.sort((a, b) => b.id - a.id);
      default:
        return filtered;
    }
  }, [activeCategory, sortBy]);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const categoryCount = (category) =>
    category === "All Products"
      ? productData.length
      : productData.filter((item) => item.category === category).length;

  const handleQuickAdd = (product) => {
    addToCart(product.id, 1, product.size[0], product.colors[0]);
    setAddedProductId(product.id);
  };

  return (
    <main className="shop" id="MainContent">
      <section className="shop__catalog" aria-labelledby="shop-results-title">
        <h2 id="shop-results-title" className="shop__visually-hidden">
          Product results
        </h2>

        <div className="shop__toolbar">
          <button
            type="button"
            className="shop__filter-toggle"
            onClick={() => setFiltersOpen(true)}
            aria-expanded={filtersOpen}
            aria-controls="shop-filter-panel"
          >
            <span>Filter{activeCategory !== "All Products" ? " (1)" : ""}</span>
            <svg viewBox="0 0 16 12" aria-hidden="true">
              <path d="M1 2h14M4 6h8M6.5 10h3" />
            </svg>
          </button>

          <p
            className="shop__result-count shop__visually-hidden"
            aria-live="polite"
          >
            {visibleProducts.length} items
          </p>

          <label className="shop__sort">
            <span>Sort by</span>
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
            >
              {sortOptions.map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {filtersOpen && (
          <button
            type="button"
            className="shop__filter-underlay"
            onClick={() => setFiltersOpen(false)}
            aria-label="Close filters"
          />
        )}

        <aside
          id="shop-filter-panel"
          className={`shop__filter-panel ${filtersOpen ? "is-open" : ""}`}
          aria-hidden={!filtersOpen}
        >
          <div className="shop__filter-head">
            <h3>{visibleProducts.length} items</h3>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              aria-label="Close filters"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <fieldset className="shop__filter-group">
            <legend>Product Type</legend>
            {categories.map((category) => (
              <label key={category}>
                <input
                  type="radio"
                  name="shop-category"
                  value={category}
                  checked={activeCategory === category}
                  onChange={() => setActiveCategory(category)}
                />
                <span>{category}</span>
                <small>{categoryCount(category)}</small>
              </label>
            ))}
          </fieldset>

          <div className="shop__filter-actions">
            <button
              type="button"
              className="shop__clear-filter"
              onClick={() => setActiveCategory("All Products")}
            >
              Clear
            </button>
            <button
              type="button"
              className="shop__apply-filter"
              onClick={() => setFiltersOpen(false)}
            >
              View {visibleProducts.length} items
            </button>
          </div>
        </aside>

        <div className="shop__grid">
          {visibleProducts.map((product) => (
            <article className="shop-card" key={product.id}>
              <div className="shop-card__media">
                <Link
                  to={`/shopDetails/${product.id}`}
                  onClick={toTop}
                  aria-label={`View ${product.name}`}
                >
                  <img
                    className="shop-card__image shop-card__image--primary"
                    src={product.images[0]}
                    alt={product.name}
                  />
                  {product.images[1] && (
                    <img
                      className="shop-card__image shop-card__image--secondary"
                      src={product.images[1]}
                      alt=""
                      aria-hidden="true"
                    />
                  )}
                </Link>

                <button
                  type="button"
                  className={`shop-card__quick-add ${
                    addedProductId === product.id ? "is-added" : ""
                  }`}
                  onClick={() => handleQuickAdd(product)}
                >
                  {addedProductId === product.id
                    ? "Added to cart"
                    : "Quick add"}
                </button>
              </div>

              <Link
                className="shop-card__information"
                to={`/shopDetails/${product.id}`}
                onClick={toTop}
              >
                <h3>{product.name}</h3>
                <p>{formatPrice(product.price)}</p>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="shop__editorial">
        <div>
          <h2>Everything You Need for Everyday Freshness</h2>
        </div>
        <p>
          Explore glass containers, sectioned lunch boxes, refrigerator organizers,
          resealable bags, silicone pouches, vacuum rolls, and cheese-care essentials.
          Every product page includes useful search terms for easy comparison.
        </p>
      </section>
    </main>
  );
}

export default Shop;
